import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  Paginated,
  PaginationQuery,
  RawPaginationMeta,
  mapPaginationMeta,
} from '../../../core/models/pagination.model';

export interface Tenant {
  id: string;
  name: string;
  business_type?: string;
  country?: string;
  phone?: string;
  settings?: Record<string, any>;
  is_active: boolean;
  users_count?: number;
  sites_count?: number;
  trial_ends_at?: string;
  created_at: string;
  updated_at: string;
}

export interface TenantsDashboard {
  total_tenants: number;
  active_tenants: number;
  inactive_tenants: number;
  total_users: number;
  total_sites: number;
  tenants_by_type?: Record<string, number>;
}

export interface CreateTenantRequest {
  name: string;
  business_type?: string;
  country?: string;
  phone?: string;
  settings?: Record<string, any>;
  is_active?: boolean;
  trial_ends_at?: string;
}

export interface UpdateTenantRequest extends Partial<CreateTenantRequest> {}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

@Injectable({
  providedIn: 'root',
})
export class TenantsService {
  private readonly apiUrl = `${environment.apiUrl}/tenants`;

  constructor(private http: HttpClient) {}

  /**
   * Get all tenants (unpaginated) — for dropdown/cross-feature consumers.
   */
  getTenants(): Observable<ApiResponse<Tenant[]>> {
    return this.http.get<ApiResponse<Tenant[]>>(this.apiUrl, {
      params: new HttpParams().set('all', 'true'),
    });
  }

  /**
   * Get a paginated page of tenants for the tenants list page.
   */
  getTenantsPage(query: PaginationQuery = {}): Observable<Paginated<Tenant>> {
    const params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('perPage', String(query.perPage ?? 25));

    return this.http
      .get<ApiResponse<Tenant[]> & { meta?: RawPaginationMeta }>(this.apiUrl, {
        params,
      })
      .pipe(
        map((res) => ({
          items: res.data ?? [],
          meta: mapPaginationMeta(res.meta, res.data?.length ?? 0),
        })),
      );
  }

  /**
   * Get dashboard statistics
   */
  getDashboard(): Observable<ApiResponse<TenantsDashboard>> {
    return this.http.get<ApiResponse<TenantsDashboard>>(
      `${this.apiUrl}/dashboard`,
    );
  }

  /**
   * Get a single tenant by ID
   */
  getTenant(id: string): Observable<ApiResponse<Tenant>> {
    return this.http.get<ApiResponse<Tenant>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create a new tenant
   */
  createTenant(data: CreateTenantRequest): Observable<ApiResponse<Tenant>> {
    return this.http.post<ApiResponse<Tenant>>(this.apiUrl, data);
  }

  /**
   * Update an existing tenant
   */
  updateTenant(
    id: string,
    data: UpdateTenantRequest,
  ): Observable<ApiResponse<Tenant>> {
    return this.http.put<ApiResponse<Tenant>>(`${this.apiUrl}/${id}`, data);
  }

  /**
   * Delete a tenant
   */
  deleteTenant(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Toggle tenant active status
   */
  toggleActive(
    id: string,
  ): Observable<ApiResponse<{ id: string; is_active: boolean }>> {
    return this.http.patch<ApiResponse<{ id: string; is_active: boolean }>>(
      `${this.apiUrl}/${id}/toggle-active`,
      {},
    );
  }
}
